import { Inject, Pipe, PipeTransform } from '@angular/core';
import { DatePipe } from '@angular/common';
import momenttz from 'moment-timezone';
import { TicketProductProperty } from '../ridango.models';
import { AppStartupService } from '../services/app-startup.service';
import { Temporal } from '@js-temporal/polyfill';
import Env from '../../env';

type FormatTimeZoneTicket = {
    product_type?: string;
    product?: {
        product_type?: string;
    } | null;
};

@Pipe({ name: 'formatTimeZone' })
export class FormatTimeZonePipe implements PipeTransform {
    constructor(
        private datePipe: DatePipe,
        @Inject('ENV') private env: Env,
        private appStartupService: AppStartupService
    ) {}

    transform(
        value: string | number | Date | null | undefined,
        format: string = 'short',
        timezone: string = 'Etc/UTC',
        fixedEndTime?: TicketProductProperty | null,
        locale?: string | null,
        applyTransportDayOffset?: boolean,
        ticket?: FormatTimeZoneTicket | null,
        productType?: string | null
    ): string | null {
        if (value == null) {
            return null;
        }

        let dateMoment = momenttz(value).tz(timezone);

        if (applyTransportDayOffset && ticket) {
            const skipResult = this.applyDurationBasedTicketOffset(dateMoment, ticket, productType, format, locale);
            if (skipResult !== null) {
                return skipResult;
            }
        }

        // Adjust ending date display when transport day ends after midnight (e.g., show 30.10 23:59 instead of 31.10 01:59)
        if (
            applyTransportDayOffset &&
            this.env.FORMATTING?.SUBTRACT_TRANSPORTATION_DAY_OFFSET_FROM?.includes('CARD_TICKETS_LIST')
        ) {
            const instant =
                value instanceof Date
                    ? Temporal.Instant.fromEpochMilliseconds(value.getTime())
                    : Temporal.Instant.from(value.toString());

            const zdt = instant.toZonedDateTimeISO(timezone);
            const transitionMinutes = this.appStartupService.getTransportationDayOffset(instant).total('minutes');
            const transitionHour = Math.floor(transitionMinutes / 60);

            if (zdt.hour < transitionHour) {
                dateMoment = dateMoment.subtract(1, 'd').hours(23).minutes(59).seconds(59);
            }
        }

        if (fixedEndTime && fixedEndTime.value) {
            // 1440 mins 24hrs
            if (fixedEndTime.value === '1440') {
                dateMoment.add(1440, 'minutes').subtract(1, 'second');
            } else if (fixedEndTime.value === '0') {
                dateMoment.subtract(1, 'second');
            } else {
                const minutesFromMidnight = parseInt(fixedEndTime.value, 10);
                const hours = Math.floor(minutesFromMidnight / 60);
                const minutes = minutesFromMidnight % 60;
                dateMoment.set({ hour: hours, minute: minutes, second: 0 });
            }
        }

        const timezoneOffset = dateMoment.format('Z');
        return this.datePipe.transform(dateMoment.toDate(), format, timezoneOffset, locale);
    }

    private applyDurationBasedTicketOffset(
        dateMoment: momenttz.Moment,
        ticket: FormatTimeZoneTicket,
        productType: string | null,
        format: string,
        locale: string | null
    ): string | null {
        const type = productType || ticket.product_type || ticket.product?.product_type;

        const shouldSkip = this.env.PURCHASE?.SKIP_OFFSET_FOR_DURATION_BASED_TICKETS ?? false;
        if (shouldSkip && type && ['M', 'H'].includes(type)) {
            const timezoneOffset = dateMoment.format('Z');
            return this.datePipe.transform(dateMoment.toDate(), format, timezoneOffset, locale);
        }

        return null;
    }
}
